import React from 'react';
import { Grid, Button } from '@material-ui/core';
import { PUBLISH_CONTENT } from 'queries/contentWriter';
import { useMutation } from '@apollo/client';
import Swal from 'sweetalert2';

import useStyles from './styles';

import { IValidationResult } from '../../../../services/ContentValidator/interfaces/validation.interface';
import { getFriendlyErrorMessage } from '../../../../utils/errorHelper';
import { StatusType } from '../../../../enums/status-type.enum';

interface IPublishContentButton {
  setValidationModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  setValidationResult: React.Dispatch<React.SetStateAction<IValidationResult>>;
  validationResult: IValidationResult;
  validate: () => IValidationResult;
  permissions: IPermission;
  user: IUser;
  contentId: string;
  setButtonsDisabled: React.Dispatch<React.SetStateAction<boolean>>;
  buttonsDisabled: boolean;
  onCompleted: (
    closeDrawer: boolean,
    autosave: boolean,
    sendWp: boolean,
  ) => void;
  onError: (message: string) => void;
  onUpdateContent: (
    status: number,
    sendWp?: boolean,
    closeDrawer?: boolean,
    autosave?: boolean,
    publishContent?: () => void,
  ) => Promise<void>;
}

export const PublishContentButton: React.FC<IPublishContentButton> = ({
  setValidationModalOpen,
  setValidationResult,
  validationResult,
  validate,
  permissions,
  user,
  contentId,
  setButtonsDisabled,
  buttonsDisabled,
  onCompleted,
  onError,
  onUpdateContent,
}) => {
  const classes = useStyles();

  const [publishContent] = useMutation(PUBLISH_CONTENT, {
    variables: {
      id: contentId,
    },
    onCompleted: () => onCompleted(true, false, true),
    onError: (error) => onError(getFriendlyErrorMessage(error)),
  });

  const confirmAlert = async (subtitle: string) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      html: subtitle,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#78001b',
      reverseButtons: true,
      confirmButtonText: 'Confirm',
      customClass: {
        container: classes.swalDialog,
      },
    });
    return result.value;
  };
  const handleClick = async () => {
    setButtonsDisabled(true);
    if (checkIfHasAnalysisErrors()) {
      openAnalysisModal();
    } else if (
      await confirmAlert('Are you sure that you want to publish this content?')
    ) {
      onUpdateContent(StatusType.WaitPublish, true, true, true, publishContent);
    } else {
      setButtonsDisabled(false);
    }
  };

  const disablePublishButton = (user: IUser, buttonsDisabled: boolean) =>
    (user.role !== 'ADMIN' || user.canPublish) && buttonsDisabled;

  const hasAnalysisErrors = (result?: IValidationResult) => {
    return result ? !!result.errors.length : !!validationResult.errors.length;
  };

  const openAnalysisModal = () => {
    setValidationModalOpen(true);
  };

  const checkIfHasAnalysisErrors = (): boolean => {
    if (!permissions.validations) return false;

    const newValidationResult = validate();
    setValidationResult(newValidationResult);

    return hasAnalysisErrors(newValidationResult);
  };

  return user.canPublish || permissions.showPublishButton ? (
    <Grid xs={12} item>
      <Button
        onClick={handleClick}
        fullWidth
        disabled={disablePublishButton(user, buttonsDisabled)}
        variant="contained"
        color="primary"
        className={
          disablePublishButton(user, buttonsDisabled)
            ? classes.buttonDisabled
            : classes.buttonPublish
        }
      >
        Publish
      </Button>
    </Grid>
  ) : (
    <></>
  );
};
